<template>
  <v-dialog v-model="dialog" persistent max-width="600">
    <v-card flat class="pa-5">
      <v-row>
        <!-- CAMERA SCANNER -->
        <v-col v-if="!physicalScanner" cols="12">
          <qrcode-stream
            v-if="dialog"
            :camera="constraints"
            :paused="paused"
            @decode="decode"
          />
        </v-col>

        <!-- PHYSICAL SCANNER -->
        <v-col v-if="physicalScanner" cols="12">
          <v-row class="mt-2 mb-2" justify="center">
            <div
              v-if="found === null && !physicalScannerError"
              class="ma-3 blue--text subtitle-1"
            >
              {{ $t("vehicle_entry.qr_scanner.physical_scanner") }}
            </div>
            <div v-if="physicalScannerError" class="ma-3 red--text subtitle-1">
              {{ $t("vehicle_entry.qr_scanner.physical_scanner_error") }}
            </div>
          </v-row>
        </v-col>

        <!-- LOADER + ALERT -->
        <v-col cols="12" class="mt-2 ml-2 mr-2">
          <!-- LOADER -->
          <v-row justify="center">
            <v-progress-circular v-if="loading" indeterminate color="primary" />
          </v-row>

          <!-- ALERT -->
          <v-row>
            <v-col cols="12">
              <v-alert
                v-model="alert.value"
                :type="alert.type"
                variant="tonal"
                border="bottom"
              >
                {{ alert.message }}
              </v-alert>
            </v-col>
          </v-row>

          <!-- ACTIONS -->
          <v-row v-if="found === true" align="center" justify="center">
            <!-- LIST -->
            <v-col cols="auto">
              <v-btn variant="outlined" color="grey-darken-2" @click="list">
                {{ $t("car_locations.qr_scanner.view_cars_list") }}
                <v-icon end> mdi-format-list-bulleted </v-icon>
              </v-btn>
            </v-col>

            <!-- EDIT -->
            <v-col cols="auto">
              <v-btn variant="outlined" color="grey-darken-2" @click="edit">
                {{ $t("car_locations.qr_scanner.edit_location") }}
                <v-icon end> mdi-pencil </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-col>

        <!-- BOTTOM ACTION BUTTONS -->
        <v-col cols="12">
          <v-row align="center">
            <v-btn variant="text" icon @click="reScan">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn variant="text" icon @click="switchScanner">
              <v-icon>mdi-qrcode</v-icon>
            </v-btn>
            <v-btn variant="text" icon @click="switchCamera">
              <v-icon>mdi-camera-switch-outline</v-icon>
            </v-btn>
            <v-btn variant="outlined" color="error" @click="close">
              {{ $t("vehicle_entry.qr_scanner.close") }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import getRequest from "../../api/getRequest";
import { QrcodeStream } from "vue3-qrcode-reader";
export default {
  components: {
    QrcodeStream,
  },
  data: () => ({
    dialog: false,
    front: false,
    paused: false,
    loading: false,
    alert: {
      value: false,
      type: "success",
      message: "",
    },
    constraints: {
      audio: false,
      video: {
        facingMode: "environment",
      },
    },
    location: {},
    found: null,
    decodedString: null,
    hideLabel: true,
    physicalScanner: false,
    physicalScannerError: false,
    buffer: "",
  }),
  methods: {
    switchCamera() {
      this.physicalScanner = false;
      document.removeEventListener("keydown", this.listenToEvent);
      this.front = !this.front;
      this.constraints = {
        video: { facingMode: this.front ? "user" : "environment" },
        audio: false,
      };
    },
    switchScanner() {
      this.physicalScanner = true;
      document.addEventListener("keydown", this.listenToEvent);
    },
    listenToEvent(e) {
      if (e.key !== "Enter") {
        this.buffer += e.key;
      } else if (this.buffer.length > 24) {
        this.physicalScannerError = true;
        this.buffer = "";
      } else {
        this.decode(this.buffer);
        this.buffer = "";
      }
    },
    async decode(value) {
      if (value !== null && value.length > 0) {
        this.loading = true;
        this.found = null;
        this.hideLabel = true;
        this.decodedString = value;
        this.paused = true;
        this.alert.value = false;

        try {
          const response = await getRequest("/car-location", `id=${value}`);
          this.location = response.carLocations;
          this.found = true;
          this.alert = {
            value: true,
            type: "success",
            message: `${this.$t("vehicle_entry.qr_scanner.found_message_1")} ${this.$t("vehicle_entry.qr_scanner.location")} ${this.$t("vehicle_entry.qr_scanner.message_2")}: ${this.decodedString}`,
          };
        } catch (error) {
          this.location = {};
          this.found = false;
          this.alert = {
            value: true,
            type: "error",
            message: `${this.$t("vehicle_entry.qr_scanner.not_found_message_1")} ${this.$t("vehicle_entry.qr_scanner.location")} ${this.$t("vehicle_entry.qr_scanner.message_2")}: ${this.decodedString}`,
          };
        }

        this.loading = false;
      }
    },
    reScan() {
      this.found = null;
      this.hideLabel = false;
      this.loading = false;
      this.paused = false;
      this.decodedString = null;
      this.physicalScannerError = false;
      this.location = {};
      this.alert = { value: true, type: "info", message: "" };
      this.alert.message = this.$t(
        "vehicle_entry.qr_scanner.scan_location_message",
      );
    },
    open() {
      this.dialog = true;
      this.alert = {
        value: true,
        type: "info",
        message: this.$t("vehicle_entry.qr_scanner.scan_location_message"),
      };
    },
    close() {
      this.dialog = false;
      this.paused = false;
      this.loading = false;
      this.location = {};
      this.found = null;
      this.decodedString = null;
      this.physicalScanner = false;
      this.physicalScannerError = false;
      this.alert = {
        value: false,
        type: "success",
        message: "",
      };
      document.removeEventListener("keydown", this.listenToEvent);
    },
    list() {
      this.$emit("list", this.location._id);
      this.close();
    },
    edit() {
      this.$emit("edit", this.location);
      this.close();
    },
  },
};
</script>
